<script setup lang="ts">
import type { Color } from '../../types/shared'

type Props = {
  size?: number | string
  color?: Color
}

const props = withDefaults(defineProps<Props>(), {
  size: 24,
  color: 'var(--ui-color-primary)',
})

const innerStyles = computed(() => {
  return {
    width: `${props.size}px`,
    height: `${props.size}px`,
  }
})

const strokeWidth = computed(() => {
  if (Number(props.size) >= 50) return 1.5

  return 3.5
})
</script>

<template>
  <span class="app-loader" data-test-id="loader" role="progressbar">
    <svg
      :style="innerStyles"
      data-test-id="loader-outer"
      class="app-loader__outer"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 43.636 43.636"
    >
      <circle
        data-test-id="loader-inner"
        class="app-loader__inner"
        fill="transparent"
        cx="50%"
        cy="50%"
        r="20"
        stroke-dasharray="125"
        stroke-dashoffset="125"
        :stroke-width="strokeWidth"
        :stroke="color"
      />
    </svg>
  </span>
</template>

<style lang="scss" scoped>
.app-loader {
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;

  &__outer {
    transition: all 0.2s ease-in-out;
    transform: rotate(-90deg);
    transform-origin: center center;
    animation: app-loader-rotate 1.4s linear infinite;
  }

  &__inner {
    z-index: 2;
    stroke-dasharray: 25, 200;
    stroke-dashoffset: 0;
    stroke-linecap: round;
    transition: all 0.2s ease-in-out, stroke-width 0s;
    transform: rotate(-90deg);
    transform-origin: center center;
    animation: app-loader-dash 1.4s ease-in-out infinite,
      app-loader-rotate 1.4s linear infinite;
  }
}

@keyframes app-loader-rotate {
  100% {
    transform: rotate(270deg);
  }
}

@keyframes app-loader-dash {
  0% {
    stroke-dasharray: 1, 200;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 100, 200;
    stroke-dashoffset: -15px;
  }
  100% {
    stroke-dasharray: 100, 200;
    stroke-dashoffset: -124px;
  }
}
</style>
